import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faLink, faServer, faSave, faEye, faEyeSlash, faPlug } from '@fortawesome/free-solid-svg-icons';
import {
  saveBillPostingSettings,
  getBillPostingSettings,
  testBillPostingConnection,
  updateBillPostingSettings,
  testRoomsConnection
} from '../Controller/BillPostingSettingsController';
import Utility from '../../../../GlobalModules/Utility/Utility';
import ModalGlobal from '../../../../GlobalModules/Utility/Components/ModalGlobal';
import ErrorBoundary from '../../../../GlobalModules/Utility/Components/ErrorBoundary';
import ConfirmationAlert from '../../../../GlobalModules/Utility/Components/ConfirmationAlert';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';

const BillPostingSettings = ({ t = (key) => key, userCredentials, token, userEntryType }) => {
  const [formData, setFormData] = useState({
    api_key: '',
    base_url: '',
    room_list_endpoint: '/rooms',
    guest_info_endpoint: '/rooms/{room_number}/guests',
    post_bill_endpoint: '/post-to-room',
    acknowledgement_status_endpoint: '/acknowledgement-status',
    health_check_endpoint: '/health',
    category_endpoint: '',
    item_master_endpoint: '',
    update_inventory_endpoint: ''
  });

  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingRooms, setTestingRooms] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [roomsConnectionStatus, setRoomsConnectionStatus] = useState(null);
  const [testingCategory, setTestingCategory] = useState(false);
  const [testingItem, setTestingItem] = useState(false);
  const [testingInventoryUpdate, setTestingInventoryUpdate] = useState(false);
  const [categoryConnectionStatus, setCategoryConnectionStatus] = useState(null);
  const [itemConnectionStatus, setItemConnectionStatus] = useState(null);
  const [inventoryUpdateConnectionStatus, setInventoryUpdateConnectionStatus] = useState(null);
  const [loadMessage, setLoadMessage] = useState(null);
  const [showRoomsStatusAlert, setShowRoomsStatusAlert] = useState(false);
  const [showInventorySection, setShowInventorySection] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Load existing settings on component mount
  useEffect(() => {
    loadBillPostingSettings();
  }, []);

  useEffect(() => {
    if (!roomsConnectionStatus) {
      return;
    }

    setShowRoomsStatusAlert(true);
    const timer = setTimeout(() => {
      setShowRoomsStatusAlert(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [roomsConnectionStatus]);

  const loadBillPostingSettings = async () => {
    try {
      // First try to load from API
      const apiResponse = await getBillPostingSettings();

      if (apiResponse.success && apiResponse.data) {
        // Data structure: apiResponse.data contains the settings directly
        const apiSettings = apiResponse.data;

        // Set the message from API response
        setLoadMessage(apiResponse.message);

        setFormData({
          api_key: apiSettings.api_key || '',
          base_url: apiSettings.base_url || '',
          room_list_endpoint: apiSettings.room_list_endpoint || '',
          guest_info_endpoint: apiSettings.guest_info_endpoint || '',
          post_bill_endpoint: apiSettings.post_bill_endpoint || '',
          acknowledgement_status_endpoint: apiSettings.acknowledgement_status_endpoint || '',
          health_check_endpoint: apiSettings.health_check_endpoint || '',
          category_endpoint: apiSettings.category_endpoint || '',
          item_master_endpoint: apiSettings.item_master_endpoint || '',
          update_inventory_endpoint: apiSettings.update_inventory_endpoint || ''
        });

        // Enable inventory section if any inventory endpoint has data
        if (apiSettings.category_endpoint || apiSettings.item_master_endpoint || apiSettings.update_inventory_endpoint) {
          setShowInventorySection(true);
        }

        // If this is a "not found" message, show it as info, otherwise clear it
        if (apiResponse.message && apiResponse.message.includes('not found')) {
          // Keep the message to show as info
        } else {
          // Clear message for existing settings
          setLoadMessage(null);
        }
      } else {
        // Fallback to localStorage or environment variables
        setLoadMessage('Loading from local storage');
        const savedSettings = {
          api_key: localStorage.getItem('billpost_api_key') || process.env.BILLPOST_API_KEY || '',
          base_url: localStorage.getItem('billpost_base_url') || process.env.BILLPOST_BASE_URL || 'https://pms-api.example.com/api/v1',
          room_list_endpoint: localStorage.getItem('billpost_room_endpoint') || '/rooms',
          guest_info_endpoint: localStorage.getItem('billpost_guest_endpoint') || '/rooms/{room_number}/guests',
          post_bill_endpoint: localStorage.getItem('billpost_bill_endpoint') || '/post-to-room',
          acknowledgement_status_endpoint: localStorage.getItem('billpost_acknowledgement_endpoint') || '/acknowledgement-status',
          health_check_endpoint: localStorage.getItem('billpost_health_endpoint') || '/health',
          category_endpoint: localStorage.getItem('billpost_category_endpoint') || '',
          item_master_endpoint: localStorage.getItem('billpost_item_master_endpoint') || '',
          update_inventory_endpoint: localStorage.getItem('billpost_update_inventory_endpoint') || ''
        };

        setFormData(savedSettings);

        // Enable inventory section if any inventory endpoint has data
        if (savedSettings.category_endpoint || savedSettings.item_master_endpoint || savedSettings.update_inventory_endpoint) {
          setShowInventorySection(true);
        }
      }
    } catch (error) {
      console.error('Error loading bill posting settings:', error);
      setLoadMessage('Failed to load bill posting settings from server');

      // Fallback to localStorage on error
      const savedSettings = {
        api_key: localStorage.getItem('billpost_api_key') || '',
        base_url: localStorage.getItem('billpost_base_url') || 'https://pms-api.example.com/api/v1',
        room_list_endpoint: localStorage.getItem('billpost_room_endpoint') || '/rooms',
        guest_info_endpoint: localStorage.getItem('billpost_guest_endpoint') || '/rooms/{room_number}/guests',
        post_bill_endpoint: localStorage.getItem('billpost_bill_endpoint') || '/post-to-room',
        acknowledgement_status_endpoint: localStorage.getItem('billpost_acknowledgement_endpoint') || '/acknowledgement-status',
        health_check_endpoint: localStorage.getItem('billpost_health_endpoint') || '/health',
        category_endpoint: localStorage.getItem('billpost_category_endpoint') || '',
        item_master_endpoint: localStorage.getItem('billpost_item_master_endpoint') || '',
        update_inventory_endpoint: localStorage.getItem('billpost_update_inventory_endpoint') || ''
      };

      setFormData(savedSettings);

      // Enable inventory section if any inventory endpoint has data
      if (savedSettings.category_endpoint || savedSettings.item_master_endpoint || savedSettings.update_inventory_endpoint) {
        setShowInventorySection(true);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const confirmationModalForSave = () => {
    setShowSaveModal(true)
  }

  const closeSaveModal = () => {
    setShowSaveModal(false)
  }

  const confirmSaveModal = async (e) => {
    closeSaveModal()
    await handleSaveSettings(e)
  }

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.api_key.trim()) {
        toast.error('API Key is required');
        setLoading(false);
        return;
      }

      if (!formData.base_url.trim()) {
        toast.error('Base URL is required');
        setLoading(false);
        return;
      }

      // Build payload: send blank inventory endpoints when Inventory Settings is disabled
      const payload = {
        ...formData,
        category_endpoint: showInventorySection ? formData.category_endpoint : '',
        item_master_endpoint: showInventorySection ? formData.item_master_endpoint : '',
        update_inventory_endpoint: showInventorySection ? formData.update_inventory_endpoint : ''
      };

      // First try to save to API
      const apiResponse = await saveBillPostingSettings(payload);

      if (apiResponse.success) {
        // API save successful
        Utility.toastNotifications(apiResponse.message || 'Bill posting settings saved successfully', 'Success', 'success');
        // toast.success(apiResponse.message || 'Bill posting settings saved successfully');
        setLoadMessage(null); // Clear any previous load messages


        // Also save to localStorage as backup
        localStorage.setItem('billpost_api_key', formData.api_key);
        localStorage.setItem('billpost_base_url', formData.base_url);
        localStorage.setItem('billpost_room_endpoint', formData.room_list_endpoint);
        localStorage.setItem('billpost_guest_endpoint', formData.guest_info_endpoint);
        localStorage.setItem('billpost_bill_endpoint', formData.post_bill_endpoint);
        localStorage.setItem('billpost_acknowledgement_endpoint', formData.acknowledgement_status_endpoint);
        localStorage.setItem('billpost_health_endpoint', formData.health_check_endpoint);
        localStorage.setItem('billpost_category_endpoint', payload.category_endpoint);
        localStorage.setItem('billpost_item_master_endpoint', payload.item_master_endpoint);
        localStorage.setItem('billpost_update_inventory_endpoint', payload.update_inventory_endpoint);

        // Update environment variables for runtime use
        process.env.BILLPOST_API_KEY = formData.api_key;
        process.env.BILLPOST_BASE_URL = formData.base_url;

        setConnectionStatus(null); // Reset connection status after settings change
      } else {
        // API save failed, show error but still save to localStorage
        // toast.error(apiResponse.message || 'Failed to save to server, saved locally only');
        Utility.toastNotifications(apiResponse.message || 'Failed to save to server, saved locally only', 'Error', 'error');
        // Save to localStorage as fallback
        localStorage.setItem('billpost_api_key', formData.api_key);
        localStorage.setItem('billpost_base_url', formData.base_url);
        localStorage.setItem('billpost_room_endpoint', formData.room_list_endpoint);
        localStorage.setItem('billpost_guest_endpoint', formData.guest_info_endpoint);
        localStorage.setItem('billpost_bill_endpoint', formData.post_bill_endpoint);
        localStorage.setItem('billpost_acknowledgement_endpoint', formData.acknowledgement_status_endpoint);
        localStorage.setItem('billpost_health_endpoint', formData.health_check_endpoint);
        localStorage.setItem('billpost_category_endpoint', payload.category_endpoint);
        localStorage.setItem('billpost_item_master_endpoint', payload.item_master_endpoint);
        localStorage.setItem('billpost_update_inventory_endpoint', payload.update_inventory_endpoint);

        // Update environment variables for runtime use
        process.env.BILLPOST_API_KEY = formData.api_key;
        process.env.BILLPOST_BASE_URL = formData.base_url;
      }
    } catch (error) {
      console.error('Error saving bill posting settings:', error);
      toast.error('Failed to save bill posting settings');

      // Save to localStorage as fallback even on error
      localStorage.setItem('billpost_api_key', formData.api_key);
      localStorage.setItem('billpost_base_url', formData.base_url);
      localStorage.setItem('billpost_room_endpoint', formData.room_list_endpoint);
      localStorage.setItem('billpost_guest_endpoint', formData.guest_info_endpoint);
      localStorage.setItem('billpost_bill_endpoint', formData.post_bill_endpoint);
      localStorage.setItem('billpost_acknowledgement_endpoint', formData.acknowledgement_status_endpoint);
      localStorage.setItem('billpost_health_endpoint', formData.health_check_endpoint);
      localStorage.setItem('billpost_category_endpoint', payload.category_endpoint);
      localStorage.setItem('billpost_item_master_endpoint', payload.item_master_endpoint);
      localStorage.setItem('billpost_update_inventory_endpoint', payload.update_inventory_endpoint);
    } finally {
      setLoading(false);
    }
  };

  const testApiConnection = async () => {
    if (!formData.api_key.trim() || !formData.base_url.trim()) {
      toast.error('Please provide API Key and Base URL before testing connection');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus(null);

    try {
      // Use the API controller to test connection
      const testResponse = await testBillPostingConnection({
        api_key: formData.api_key,
        base_url: formData.base_url,
        health_check_endpoint: formData.health_check_endpoint
      });

      if (testResponse.success) {
        setConnectionStatus({
          success: true,
          message: testResponse.message || 'Connection successful! PMS API is reachable.',
          timestamp: new Date().toLocaleString()
        });
        toast.success('API connection test successful');
      } else {
        setConnectionStatus({
          success: false,
          message: testResponse.message || 'Connection test failed',
          timestamp: new Date().toLocaleString()
        });
        toast.error('API connection test failed');
      }
    } catch (error) {
      setConnectionStatus({
        success: false,
        message: `Connection failed: ${error.message}`,
        timestamp: new Date().toLocaleString()
      });
      toast.error('API connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const testRoomsConnectionHandler = async () => {
    if (!formData.api_key.trim() || !formData.room_list_endpoint.trim()) {
      toast.error('Please provide API Key and Room List Endpoint before testing rooms connection');
      return;
    }

    setTestingRooms(true);
    setRoomsConnectionStatus(null);

    try {
      // Use the new API endpoint with current form values
      const connectionData = {
        api_key: formData.api_key,
        base_url: formData.base_url,
        room_list_endpoint: formData.room_list_endpoint
      };

      // Test the rooms API call using the controller
      const roomsResponse = await testRoomsConnection(connectionData);

      if (roomsResponse.success) {
        const roomCount = Array.isArray(roomsResponse.data) ? roomsResponse.data.length : 0;
        setRoomsConnectionStatus({
          success: true,
          message: `Rooms API connection successful! Found ${roomCount} rooms.`,
          timestamp: new Date().toLocaleString(),
          data: roomsResponse.data
        });
        // toast.success(`Rooms API test successful - ${roomCount} rooms found`);
      } else {
        setRoomsConnectionStatus({
          success: false,
          message: roomsResponse.message || 'Rooms API connection failed',
          timestamp: new Date().toLocaleString()
        });
        // toast.error('Rooms API connection test failed');
      }
    } catch (error) {
      setRoomsConnectionStatus({
        success: false,
        message: `Rooms API connection failed: ${error.message}`,
        timestamp: new Date().toLocaleString()
      });
      toast.error('Rooms API connection test failed');
    } finally {
      setTestingRooms(false);
    }
  };

  const testCategoryEndpointHandler = async () => {
    if (!formData.api_key.trim() || !formData.base_url.trim() || !formData.category_endpoint.trim()) {
      toast.error('Please provide API Key, Base URL, and Category Endpoint before testing');
      return;
    }

    setTestingCategory(true);
    setCategoryConnectionStatus(null);

    try {
      const connectionData = {
        api_key: formData.api_key,
        base_url: formData.base_url,
        category_endpoint: formData.category_endpoint
      };

      const testResponse = await testBillPostingConnection(connectionData);

      if (testResponse.success) {
        setCategoryConnectionStatus({
          success: true,
          message: 'Category endpoint connection successful!',
          timestamp: new Date().toLocaleString()
        });
        toast.success('Category endpoint test successful');
      } else {
        setCategoryConnectionStatus({
          success: false,
          message: testResponse.message || 'Category endpoint connection failed',
          timestamp: new Date().toLocaleString()
        });
        toast.error('Category endpoint test failed');
      }
    } catch (error) {
      console.error('Error testing category endpoint:', error);
      setCategoryConnectionStatus({
        success: false,
        message: error.message || 'An error occurred while testing category endpoint',
        timestamp: new Date().toLocaleString()
      });
      toast.error('Category endpoint test failed');
    } finally {
      setTestingCategory(false);
    }
  };

  const testItemEndpointHandler = async () => {
    if (!formData.api_key.trim() || !formData.base_url.trim() || !formData.item_master_endpoint.trim()) {
      toast.error('Please provide API Key, Base URL, and Item Endpoint before testing');
      return;
    }

    setTestingItem(true);
    setItemConnectionStatus(null);

    try {
      const connectionData = {
        api_key: formData.api_key,
        base_url: formData.base_url,
        item_master_endpoint: formData.item_master_endpoint
      };

      const testResponse = await testBillPostingConnection(connectionData);

      if (testResponse.success) {
        setItemConnectionStatus({
          success: true,
          message: 'Item endpoint connection successful!',
          timestamp: new Date().toLocaleString()
        });
        toast.success('Item endpoint test successful');
      } else {
        setItemConnectionStatus({
          success: false,
          message: testResponse.message || 'Item endpoint connection failed',
          timestamp: new Date().toLocaleString()
        });
        toast.error('Item endpoint test failed');
      }
    } catch (error) {
      console.error('Error testing item endpoint:', error);
      setItemConnectionStatus({
        success: false,
        message: error.message || 'An error occurred while testing item endpoint',
        timestamp: new Date().toLocaleString()
      });
      toast.error('Item endpoint test failed');
    } finally {
      setTestingItem(false);
    }
  };

  const testInventoryUpdateEndpointHandler = async () => {
    if (!formData.api_key.trim() || !formData.base_url.trim() || !formData.update_inventory_endpoint.trim()) {
      // toast.error('Please provide API Key, Base URL, and Inventory Update Endpoint before testing');
      Utility.toastNotifications('Please provide API Key, Base URL, and Inventory Update Endpoint before testing', 'Error', 'error');
      return;
    }

    setTestingInventoryUpdate(true);
    setInventoryUpdateConnectionStatus(null);

    try {
      const connectionData = {
        api_key: formData.api_key,
        base_url: formData.base_url,
        update_inventory_endpoint: formData.update_inventory_endpoint
      };

      const testResponse = await testBillPostingConnection(connectionData);

      if (testResponse.success) {
        setInventoryUpdateConnectionStatus({
          success: true,
          message: 'Inventory Update endpoint connection successful!',
          timestamp: new Date().toLocaleString()
        });
        Utility.toastNotifications('Inventory Update endpoint test successful', 'Success', 'success');
      } else {
        setInventoryUpdateConnectionStatus({
          success: false,
          message: testResponse.message || 'Inventory Update endpoint connection failed',
          timestamp: new Date().toLocaleString()
        });
        Utility.toastNotifications('Inventory Update endpoint test failed', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error testing inventory update endpoint:', error);
      setInventoryUpdateConnectionStatus({
        success: false,
        message: error.message || 'An error occurred while testing inventory update endpoint',
        timestamp: new Date().toLocaleString()
      });
      Utility.toastNotifications('Inventory Update endpoint test failed', 'Error', 'error');
    } finally {
      setTestingInventoryUpdate(false);
    }
  };

  const resetToDefaults = () => {
    setFormData({
      api_key: '',
      base_url: 'https://pms-api.example.com/api/v1',
      room_list_endpoint: '/rooms',
      guest_info_endpoint: '/rooms/{room_id}/guests',
      post_bill_endpoint: '/post-to-room',
      acknowledgement_status_endpoint: '/acknowledgement-status',
      health_check_endpoint: '/health'
    });
    setConnectionStatus(null);
    setRoomsConnectionStatus(null);
  };

  //======= add action permission ====//
  // Check multiple permissions at once
  const billPostingSettingsPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'bill_posting_settings',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept', 'update'],
    userCredentials?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  return (
    <Card className="bill-posting-settings">
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faServer} className="me-2" />
          {t('PMS Integration Settings')}
        </h5>
      </Card.Header>
      <Card.Body>
        {/* Load Status Message */}
        {loadMessage && (
          <Alert
            variant={loadMessage.includes('not found') ? 'info' : loadMessage.includes('Failed') ? 'warning' : 'info'}
            className="mb-3"
          >
            {loadMessage}
          </Alert>
        )}

        <Form onSubmit={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      confirmationModalForSave()
                    }}>
          {/* API Configuration Section */}
          <div className="mb-4">
            <h6 className="text-muted mb-3">
              <FontAwesomeIcon icon={faKey} className="me-2" />
              {t('API Authentication')}
            </h6>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('API Key')} <span className="text-danger">*</span></Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showApiKey ? "text" : "password"}
                      name="api_key"
                      value={formData.api_key}
                      onChange={handleInputChange}
                      placeholder={t('Enter PMS API Key')}
                      required
                    />
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      <FontAwesomeIcon icon={showApiKey ? faEye : faEyeSlash} />
                    </Button>
                  </div>
                  <Form.Text className="text-muted">
                    {t('API key provided by your Property Management System')}
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('Base URL')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="url"
                    name="base_url"
                    value={formData.base_url}
                    onChange={handleInputChange}
                    placeholder="https://pms-api.example.com/api/v1"
                    required
                  />
                  <Form.Text className="text-muted">
                    {t('Base URL of your PMS API (including protocol and version)')}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Bill Posting Settings Section */}
          <div className="mb-4">
            <h6 className="text-muted mb-3">
              <FontAwesomeIcon icon={faLink} className="me-2" />
              {t('Bill Posting Settings')}
            </h6>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('Room List Endpoint')} <span className="text-danger">*</span></Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      name="room_list_endpoint"
                      value={formData.room_list_endpoint}
                      onChange={handleInputChange}
                      placeholder="/rooms"
                      required
                    />
                    {formData.api_key && formData.room_list_endpoint && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={testRoomsConnectionHandler}
                        disabled={testingRooms}
                        title="Test Rooms API Connection"
                      >
                        {testingRooms ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faPlug} />
                        )}
                      </Button>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    {t('Endpoint to fetch available rooms')}
                  </Form.Text>


                </Form.Group>
              </Col>

              <Col md={6}>
                {/* Rooms Connection Status */}
                {roomsConnectionStatus && showRoomsStatusAlert && (
                  <Alert
                    variant={roomsConnectionStatus.success ? 'success' : 'danger'}
                    className=" mb-0" style={{ marginTop: '25px' }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>
                          {roomsConnectionStatus.success ? '✓ API Connection Successfull' : '✗ API Connection Failed'}
                        </strong>
                        {/* <div className="small">{roomsConnectionStatus.message}</div> */}
                        {roomsConnectionStatus.success && roomsConnectionStatus.data && (
                          <div className="small text-muted mt-1">
                            {/* Sample rooms: {roomsConnectionStatus.data.slice(0, 3).map(room => room.room_number || room.name || room.id).join(', ')} */}
                            {/* {roomsConnectionStatus.data.length > 3 && '...'} */}
                          </div>
                        )}
                      </div>
                      <small className="text-muted">{roomsConnectionStatus.timestamp}</small>
                    </div>
                  </Alert>
                )}
              </Col>

              {/* <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('Guest Information Endpoint')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="guest_info_endpoint"
                    value={formData.guest_info_endpoint}
                    onChange={handleInputChange}
                    placeholder="/rooms/{room_number}/guests"
                  />
                  <Form.Text className="text-muted">
                    {t('Endpoint to fetch guests by room ID (use {room_number} as placeholder)')}
                  </Form.Text>
                </Form.Group>
              </Col> */}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('Post Bill to Room Endpoint')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="post_bill_endpoint"
                    value={formData.post_bill_endpoint}
                    onChange={handleInputChange}
                    placeholder="/post-to-room"
                    required
                  />
                  <Form.Text className="text-muted">
                    {t('Endpoint to post bill to room/guests')}
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('Acknowledgement Status Endpoint')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="acknowledgement_status_endpoint"
                    value={formData.acknowledgement_status_endpoint}
                    onChange={handleInputChange}
                    placeholder="/acknowledgement-status"
                    required
                  />
                  <Form.Text className="text-muted">
                    {t('Endpoint to check bill posting acknowledgement status')}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Inventory Settings Section */}
          <div className="mb-4" style={{ borderTop: '2px solid #e9ecef', paddingTop: '20px' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-muted mb-0">
                <FontAwesomeIcon icon={faLink} className="me-2" />
                {t('Inventory Settings')}
              </h6>
              <Form.Check
                type="switch"
                id="inventory-toggle"
                label=""
                checked={showInventorySection}
                onChange={(e) => setShowInventorySection(e.target.checked)}
              />
            </div>

            {showInventorySection && (
              <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('PMS Category Endpoint')}</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      name="category_endpoint"
                      value={formData.category_endpoint}
                      onChange={handleInputChange}
                      placeholder="/category"
                    />
                    {/* {formData.api_key && formData.base_url && formData.category_endpoint && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={testCategoryEndpointHandler}
                        disabled={testingCategory}
                        title="Test Category Endpoint"
                      >
                        {testingCategory ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faPlug} />
                        )}
                      </Button>
                    )} */}
                  </div>
                  <Form.Text className="text-muted">
                    {t('Endpoint to sync categories with PMS')}
                  </Form.Text>
                  {categoryConnectionStatus && (
                    <Alert
                      variant={categoryConnectionStatus.success ? 'success' : 'danger'}
                      className="mt-2 mb-0"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <strong>{categoryConnectionStatus.success ? '✓ ' : '✗ '}</strong>
                      {categoryConnectionStatus.message}
                    </Alert>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('PMS Item Endpoint')}</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      name="item_master_endpoint"
                      value={formData.item_master_endpoint}
                      onChange={handleInputChange}
                      placeholder="/items"
                    />
                    {/* {formData.api_key && formData.base_url && formData.item_master_endpoint && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={testItemEndpointHandler}
                        disabled={testingItem}
                        title="Test Item Endpoint"
                      >
                        {testingItem ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faPlug} />
                        )}
                      </Button>
                    )} */}
                  </div>
                  <Form.Text className="text-muted">
                    {t('Endpoint to sync items/products with PMS')}
                  </Form.Text>
                  {itemConnectionStatus && (
                    <Alert
                      variant={itemConnectionStatus.success ? 'success' : 'danger'}
                      className="mt-2 mb-0"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <strong>{itemConnectionStatus.success ? '✓ ' : '✗ '}</strong>
                      {itemConnectionStatus.message}
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('PMS Inventory Update Endpoint')}</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      name="update_inventory_endpoint"
                      value={formData.update_inventory_endpoint}
                      onChange={handleInputChange}
                      placeholder="/update-inventory"
                    />
                    {/* {formData.api_key && formData.base_url && formData.update_inventory_endpoint && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={testInventoryUpdateEndpointHandler}
                        disabled={testingInventoryUpdate}
                        title="Test Inventory Update Endpoint"
                      >
                        {testingInventoryUpdate ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faPlug} />
                        )}
                      </Button>
                    )} */}
                  </div>
                  <Form.Text className="text-muted">
                    {t('Endpoint to update inventory in PMS')}
                  </Form.Text>
                  {inventoryUpdateConnectionStatus && (
                    <Alert
                      variant={inventoryUpdateConnectionStatus.success ? 'success' : 'danger'}
                      className="mt-2 mb-0"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <strong>{inventoryUpdateConnectionStatus.success ? '✓ ' : '✗ '}</strong>
                      {inventoryUpdateConnectionStatus.message}
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>
              </>
            )}
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <Alert variant={connectionStatus.success ? 'success' : 'danger'} className="mb-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{connectionStatus.success ? t('Connection Successful') : t('Connection Failed')}</strong>
                  <div>{connectionStatus.message}</div>
                </div>
                <small className="text-muted">{connectionStatus.timestamp}</small>
              </div>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-between align-items-center">
            {/* <div>
              <Button
                variant="outline-primary"
                type="button"
                onClick={testApiConnection}
                disabled={testingConnection || !formData.api_key || !formData.base_url}
                className="me-2"
              >
                {testingConnection ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t('Testing Connection...')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faServer} className="me-2" />
                    {t('Test Connection')}
                  </>
                )}
              </Button>
              
              <Button
                variant="outline-secondary"
                type="button"
                onClick={resetToDefaults}
              >
                {t('Reset to Defaults')}
              </Button>
            </div> */}

            {billPostingSettingsPermissions.update && (
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t('Saving...')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {t('Save Settings')}
                  </>
                )}
              </Button>
            )}
          </div>
        </Form>

        {/* Configuration Preview */}
        {/* <div className="mt-4 pt-3 border-top">
          <h6 className="text-muted mb-3">{t('Configuration Preview')}</h6>
          <div className="bg-light p-3 rounded">
            <Row>
              <Col md={6}>
                <small className="text-muted d-block">{t('Full Room List URL:')}</small>
                <code className="small">{formData.base_url}{formData.room_list_endpoint}</code>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">{t('Full Guest Info URL:')}</small>
                <code className="small">{formData.base_url}{formData.guest_info_endpoint}</code>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <small className="text-muted d-block">{t('Full Post Bill URL:')}</small>
                <code className="small">{formData.base_url}{formData.post_bill_endpoint}</code>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">{t('Full Acknowledgement Status URL:')}</small>
                <code className="small">{formData.base_url}{formData.acknowledgement_status_endpoint}</code>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <small className="text-muted d-block">{t('Full Health Check URL:')}</small>
                <code className="small">{formData.base_url}{formData.health_check_endpoint}</code>
              </Col>
            </Row>
          </div>
        </div> */}
      </Card.Body>

      <ErrorBoundary title="ModalGlobal Error">
        <ModalGlobal
          show={showSaveModal}
          onHide={closeSaveModal}
          className="modalcustomize confirmationalertmodalDialog"
          bodyClassName="cancelConfirmationbody"
          headerclassName="close_btn_icon"
          title="Confirm Save Settings"
          footer={false}
          body={
            <>
              <ErrorBoundary title="ConfirmationAlert Error">
                <ConfirmationAlert
                  BodyFirstContent="Are you sure you want to save these bill posting settings?"
                  BodySecondContent=""
                  BodyThirdContent=""
                  confirmationButtonContent="Yes"
                  cancelButtonContent="No"
                  deleteConfirmButton={confirmSaveModal}
                  deleteCancleButton={closeSaveModal}
                />
              </ErrorBoundary>
            </>
          }
        />
      </ErrorBoundary>
    </Card>
  );
};

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType
  }
}

export default connect(mapStateToProps)(withNamespaces()(BillPostingSettings))

